import { Company, ProductTier, User, UserMode } from '../../lib';
import { Permission } from '../permission';

type PermissionsProps = {
	[key: string]: (props: { user?: User; company?: Company }) => boolean;
};

// Orthogonal tier model: each permission explicitly defines which tiers have access
// TierAlpha (Team plan) has restricted access to top-down recognition features compared to TierThree (Organization plan)

export const PermissionValidations: PermissionsProps = {
	// One-on-Ones feature access - available on Organization (TierThree) and Team (TierAlpha) plans
	// NOTE: Currently gates ALL OF THESE: One-on-Ones, Performance Recaps and Feedback. Consider separating during legacy migration.
	// limited_release_mode allows companies to pilot features with specific users before full rollout
	[Permission.CanAccessOneOnOnes]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier) && !company?.limited_release_mode,

	[Permission.CanAccessGoals]: ({ company }) =>
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierAlpha, ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),
	// home related validations
	[Permission.CanGiveRecognition]: ({ company, user }) =>
		// NOTE: can_give is true if company allows non-zero giving OR if user mode is normal, benefactor or bot
		user?.can_give === true &&
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanAccessUserProfile]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanAccessAnnouncements]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAdministerAnnouncements]: ({ user }) => user?.global_admin === true || user?.announcements_admin === true,

	[Permission.CanAccessAnalytics]: ({ company }) => !!company?.product_tier && [ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAccessAdminOnlySettings]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanViewStrongerConnections]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanViewManagerGiving]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	// right sidebar related validations
	[Permission.CanViewHashtagSummary]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	// user summary aka most recognized widget
	[Permission.CanViewUserSummary]: ({ company, user }) =>
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier) &&
		!(company?.hide_competitive_features && !user?.global_admin),

	// reward related validations
	[Permission.CanClaimPointsBoost]: ({ company, user }) =>
		user?.user_mode !== UserMode.Observer &&
		user?.user_mode !== UserMode.Receiver &&
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	// admin related validations
	[Permission.CanViewAdmin]: ({ user }) =>
		!!user?.admin || !!user?.global_admin || !!user?.awards_admin || !!user?.rewards_admin || !!user?.finance_admin || !!user?.tech_admin,

	[Permission.CanManageAwards]: ({ user }) => !!user?.admin || !!user?.global_admin || !!user?.awards_admin,

	[Permission.CanViewContinuousCheckIns]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAccessUserManagement]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanManageUserImport]: ({ company }) =>
		!company?.user_management_integration_url &&
		!!company?.product_tier &&
		[ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanViewPointsReports]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	// can manage show departments on analytics leaderboard checkbox > tier 1
	[Permission.CanManageShowDepartmentsOnLeaderboard]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	[Permission.CanManagePointsBoost]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	// super admin related validation
	[Permission.CanViewSuperAdmin]: ({ user }) => !!user?.show_superadmin_feature,

	// admin programs validations
	[Permission.CanManagePeerToPeer]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),

	// Top-down recognition feature validations - available to all tiers except TierAlpha (Team plan)
	[Permission.CanAccessAwards]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAccessNominations]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAccessIncentives]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierOne, ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	[Permission.CanAccessFlashPoints]: ({ company }) => !!company?.product_tier && [ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),

	// Manager enablement validations
	[Permission.CanAccessCheckIn]: ({ company }) =>
		!!company?.product_tier && [ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),
	[Permission.CanAccessTeamHub]: ({ company, user }) =>
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier) &&
		user?.is_manager === true,
	[Permission.CanAccessAdminCheckInControls]: ({ company, user }) =>
		!!company?.product_tier &&
		[ProductTier.TierFree, ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier) &&
		user?.global_admin === true,

	// video related validations
	[Permission.CanManageVideoUploads]: ({ company }) => !company?.disable_video_uploads,
	[Permission.CanRecordVideo]: ({ company }) =>
		// If the company has allowed video uploading OR recording
		(!company?.disable_video_recordings || !company?.disable_video_uploads) &&
		// And they're not on the Appreciate package
		!!company?.product_tier &&
		[ProductTier.TierTwo, ProductTier.TierThree, ProductTier.TierAlpha].includes(company.product_tier),
	[Permission.CanAccessVideoCarousel]: ({ company }) =>
		// If the company has allowed video uploading OR recording
		(!company?.disable_video_recordings || !company?.disable_video_uploads) &&
		// Video carousel is only available for TierTwo and TierThree, not TierAlpha (Team plan)
		!!company?.product_tier &&
		[ProductTier.TierTwo, ProductTier.TierThree].includes(company.product_tier),
};
